"use client";

import { cn } from "@/lib/utils";
import { IPages, IUserData } from "@/types";
import {
    BORDER_RADIUS_OPTIONS,
    SHADOW_OPTIONS,
} from "./blocks/utils/block-style-classes";
import Link from "next/link";
import { IconHome, IconMenu2 } from "@tabler/icons-react";
import { Separator } from "../ui/separator";
import { usePathname } from "next/navigation";
import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Button } from "../ui/button";
import { RevalidateLink } from "../admin/layout/RevalidateLink";
import ProfileShare from "../admin/profile/ProfileShare";
import { UserImage } from "./heading/index";
import DorasIcon from "../marketing/dorasicon";

interface Props {
    account: IUserData;
    theme: IUserData["chosen_theme"];
    pages: IPages[];
    admin?: boolean;
    domain: boolean;
}

export default function BaseNavigation({
    account,
    theme,
    pages,
    admin,
    domain,
}: Props) {
    const path = usePathname();
    const pagemap = pages
        .filter((e) => e.enabled)
        .sort((a, b) => a.sort - b.sort);

    const navItems = [
        {
            id: "home",
            slug: "",
            name: <IconHome size={24} />,
            href: admin
                ? "/admin/content"
                : domain
                    ? `/`
                    : `/${account.username}`,
        },
        // Only show first 2 pages in the horizontal navigation
        ...pagemap.slice(0, 3).map((page) => ({
            id: page.id,
            slug: page.slug,
            name: page.name,
            href: admin
                ? `/admin/content/${page.slug}`
                : domain
                    ? `/${page.slug}`
                    : `/${account.username}/${page.slug}`,
        })),
    ];

    const activeIndex = navItems.findIndex((item) => path === item.href);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    const navRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);
    const [padding, setPadding] = useState({ left: 0, top: 0 });

    useEffect(() => {
        if (navRef.current) {
            const navStyle = window.getComputedStyle(navRef.current);
            setPadding({
                left: parseFloat(navStyle.paddingLeft),
                top: parseFloat(navStyle.paddingTop),
            });
        }
    }, []);

    useEffect(() => {
        itemRefs.current = itemRefs.current.slice(0, navItems.length);
    }, [navItems.length]);

    const navRect = navRef.current?.getBoundingClientRect();
    const activeRect =
        activeIndex !== -1
            ? itemRefs.current[activeIndex]?.getBoundingClientRect()
            : null;
    const hoveredRect =
        hoveredIndex !== null
            ? itemRefs.current[hoveredIndex]?.getBoundingClientRect()
            : null;

    return (
        <div
            ref={navRef}
            onPointerLeave={() => setHoveredIndex(null)}
            className={cn(
                "relative mx-auto flex w-full max-w-2xl shrink-0 items-center gap-3 overflow-x-auto border border-user-tertiary-color bg-user-secondary-color p-2",
                BORDER_RADIUS_OPTIONS[theme.profile.design.corner_radius].class,
                SHADOW_OPTIONS[theme.profile.design.shadow].class
            )}
            style={{ fontFamily: theme.profile.design.font }}
            id="base-navigation"
        >
            <AnimatePresence>
                {activeRect && navRect && !hoveredRect && (
                    <motion.div
                        className={cn(
                            "absolute z-10 bg-user-primary-color",
                            BORDER_RADIUS_OPTIONS[
                                theme.profile.design.corner_radius
                            ].class
                        )}
                        initial={{
                            x: activeRect.left - navRect.left - padding.left,
                            y: activeRect.top - navRect.top - padding.top,
                            width: activeRect.width,
                            height: activeRect.height,
                        }}
                        animate={{
                            x: activeRect.left - navRect.left - padding.left,
                            y: activeRect.top - navRect.top - padding.top,
                            width: activeRect.width,
                            height: activeRect.height,
                            transition: {
                                type: "tween",
                                ease: "easeOut",
                                duration: 0.2,
                            },
                        }}
                    />
                )}
                {hoveredRect && navRect && (
                    <motion.div
                        className={cn(
                            "absolute z-10 bg-user-primary-color",
                            BORDER_RADIUS_OPTIONS[
                                theme.profile.design.corner_radius
                            ].class
                        )}
                        initial={{
                            x: hoveredRect.left - navRect.left - padding.left,
                            y: hoveredRect.top - navRect.top - padding.top,
                            width: hoveredRect.width,
                            height: hoveredRect.height,
                        }}
                        animate={{
                            x: hoveredRect.left - navRect.left - padding.left,
                            y: hoveredRect.top - navRect.top - padding.top,
                            width: hoveredRect.width,
                            height: hoveredRect.height,
                            transition: {
                                type: "tween",
                                ease: "easeOut",
                                duration: 0.2,
                            },
                        }}
                    />
                )}
            </AnimatePresence>
            <div
                className="flex h-10 w-full items-center gap-2 overflow-x-auto"
                onScroll={() => {
                    // This is a hack to force a re-render on scroll to update the bounding rects
                    // A better solution might be needed if performance is an issue
                    setHoveredIndex(hoveredIndex);
                }}
            >
                {navItems.map((item, index) => (
                    <a
                        ref={(el) => {
                            itemRefs.current[index] = el;
                        }}
                        key={item.id}
                        href={item.href}
                        onPointerEnter={() => setHoveredIndex(index)}
                        className={cn(
                            "relative z-20 flex items-center gap-2 text-nowrap p-2 font-semibold",
                            hoveredIndex === index || activeIndex === index
                                ? "text-user-primary-text-color"
                                : "text-user-secondary-text-color"
                        )}
                    >
                        {item.name}
                    </a>
                ))}
            </div>
            {/* <Separator
                orientation="vertical"
                className="ml-auto h-6 bg-user-secondary-text-color"
            /> */}
            <div className="flex w-8 items-center gap-2">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            size="icon"
                            className={cn(
                                "ml-auto h-auto w-auto p-1 transition-all duration-100 hover:scale-105",
                                BORDER_RADIUS_OPTIONS[
                                    theme.profile.design.corner_radius
                                ].class
                            )}
                            style={{
                                background: theme.profile.colors.primary,
                                color: theme.profile.colors.primary_text,
                            }}
                            id="user-navigation-button"
                        >
                            <IconMenu2 size={24} />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        className="min-w-60 max-w-96 border border-user-tertiary-color text-xl shadow-xl"
                        side="top"
                        sideOffset={18}
                        style={{
                            background:
                                theme.profile.colors.secondary ||
                                theme.profile.colors.primary,
                            color: theme.profile.colors.primary_text,
                            fontFamily: theme.profile.design.font,
                        }}
                        id="user-navigation-content"
                    >
                        <DropdownMenuLabel className="flex flex-col items-start justify-start gap-1 text-xl">
                            <div className="flex items-center gap-1">
                                <UserImage
                                    account={account}
                                    className="size-10"
                                    theme={theme}
                                />
                                <p className="truncate">
                                    {account.displayname}
                                </p>
                            </div>
                            {/* <DropdownMenuItem asChild>
                            <ProfileShare
                                theme={theme}
                                user={account}
                                admin={admin}
                            />
                        </DropdownMenuItem> */}
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel
                            className={cn(
                                "justify-normal gap-1",
                                pages.length == 0 && "hidden"
                            )}
                        >
                            {pages.length == 0 ? null : (
                                <p className="truncate">My Pages</p>
                            )}
                            {/* <p className="truncate">My Pages</p> */}
                        </DropdownMenuLabel>

                        {/* Map of pages */}
                        <div className="max-h-48 w-full overflow-auto">
                            <RevalidateLink
                                href={
                                    admin
                                        ? "/admin/content"
                                        : domain
                                            ? `/`
                                            : `/${account.username}`
                                }
                                className={`flex px-2 py-1 text-lg hover:underline`}
                            >
                                <p className="truncate">Home</p>
                            </RevalidateLink>
                            {pagemap.map((page, index) => (
                                <RevalidateLink
                                    key={index}
                                    href={
                                        admin
                                            ? `/admin/content/${page.slug}`
                                            : domain
                                                ? `/${page.slug}`
                                                : `/${account.username}/${page.slug}`
                                    }
                                    className={`flex px-2 py-1 text-lg hover:underline`}
                                >
                                    <p className="truncate">{page.name}</p>
                                </RevalidateLink>
                            ))}
                            <div className="!visible rounded-md !bg-card !text-foreground">
                                <RevalidateLink
                                    href={`https://doras.to/report?user=${account.id}`}
                                    className={`flex px-2 py-1 text-lg hover:underline`}
                                >
                                    <p className="truncate">Report</p>
                                </RevalidateLink>
                            </div>
                        </div>
                        {account.settings.branding == false && (
                            <>
                                <DropdownMenuSeparator />
                                <div className="flex flex-col items-center">
                                    <a
                                        href={
                                            account.affiliate == null
                                                ? "https://doras.to/"
                                                : "https://doras.to/?aff=" +
                                                account.affiliate
                                        }
                                        target="_blank"
                                    >
                                        <Button className="gap-3 bg-user-primary-color font-bold text-user-primary-text-color">
                                            <DorasIcon
                                                size={24}
                                                color="var(--user-primary-text-color)"
                                            />
                                            Create your profile
                                        </Button>
                                    </a>
                                </div>
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}
